import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import type { DailyFeedbackResponse } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'

/** One day's feedback tally. `day` is an ISO calendar date (`YYYY-MM-DD`,
 * UTC) so the series is timezone-stable and trivially comparable in tests. */
export type FeedbackDailyPoint = {
  day: string
  positive: number
  negative: number
}

export type FeedbackDailyState =
  | { kind: 'loading' }
  | { kind: 'ready'; points: FeedbackDailyPoint[] }
  | { kind: 'error'; message: string }

export type UseFeedbackDailyOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  fetchImpl?: typeof fetch
  /** Inclusive range bounds. */
  from: Date
  to: Date
  /** Gate the load on the caller being a confirmed admin — the endpoint is
   * admin-only and would 403 otherwise. */
  enabled?: boolean
}

export type UseFeedbackDailyResult = {
  state: FeedbackDailyState
  refresh: () => Promise<void>
}

function toIsoDay(value: Date): string {
  return value.toISOString().slice(0, 10)
}

function toPoint(response: DailyFeedbackResponse): FeedbackDailyPoint {
  return {
    day: toIsoDay(response.day),
    positive: response.positive,
    negative: response.negative,
  }
}

export function useFeedbackDaily(options: UseFeedbackDailyOptions): UseFeedbackDailyResult {
  const enabled = options.enabled ?? true
  const [state, setState] = useState<FeedbackDailyState>({ kind: 'loading' })
  const abortRef = useRef<AbortController | null>(null)
  const api = useAnswerApi(options)

  const fromTime = options.from.getTime()
  const toTime = options.to.getTime()

  const refresh = useCallback(async () => {
    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller
    try {
      const rows = await api.feedbackDaily(
        { from: new Date(fromTime), to: new Date(toTime) },
        { signal: controller.signal },
      )
      if (controller.signal.aborted) return
      setState({ kind: 'ready', points: rows.map(toPoint) })
    } catch (err) {
      if (controller.signal.aborted) return
      const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
      setState({ kind: 'error', message: apiErr.message })
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [api, fromTime, toTime])

  const refreshRef = useRef(refresh)
  useEffect(() => {
    refreshRef.current = refresh
  })
  useEffect(() => {
    if (!enabled) return
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }))
    void refreshRef.current()
    return () => {
      abortRef.current?.abort()
    }
  }, [enabled, fromTime, toTime])

  return { state, refresh }
}
